import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { IPackagingStockCountDetailedItem } from 'src/types/packaging-stock-count';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemCode', label: 'Item Code', align: 'left', primary: true },
  { id: 'itemName', label: 'Item Name', align: 'left', primary: true, minWidth: 220 },
  { id: 'previousQuantity', label: 'Previous', align: 'left', primary: true },
  { id: 'addedQuantity', label: 'Added', align: 'left', primary: true },
  { id: 'newQuantity', label: 'New Total', align: 'left', primary: true },
];

type Props = {
  currentStockCount: IPackagingStockCountDetailedItem;
};

export default function PackagingStockCountDetails({ currentStockCount }: Props) {
  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentStockCount.stockCountCode}
                secondary="Stock Count No"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentStockCount.countDate)}
                secondary="Count Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentStockCount.items.length}
                secondary="Item Lines"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            {currentStockCount.createdBy && (
              <Grid xs={12} md={6}>
                <ListItemText
                  primary={`${currentStockCount.createdBy.firstName}${
                    currentStockCount.createdBy.lastName
                      ? ` ${currentStockCount.createdBy.lastName}`
                      : ''
                  }`}
                  secondary="Counted By"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>
            )}

            {currentStockCount.remarks && (
              <Grid xs={12}>
                <ListItemText
                  primary={currentStockCount.remarks}
                  secondary="Remarks"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 680 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentStockCount.items.map((item) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.packagingItem.view(item.packagingItem._id)}
                            >
                              {item.packagingItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.packagingItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.previousQuantity}
                          </TableCell>

                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                              color: 'success.main',
                            }}
                          >
                            +{item.addedQuantity}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.newQuantity}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            <Link component={RouterLink} href={paths.dashboard.packagingStockCount.root}>
              <Button variant="outlined" color="inherit">
                Back
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
